import React, { useEffect, useRef, useState } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { useSidebar } from '../context/SidebarContext';
import api from '../utils/api';

const AUTO_KOT_PRINTED_KEY = 'ely:auto-kot:printed:v1';
const AUTO_KOT_LOCK_PREFIX = 'ely:auto-kot:lock:';
const AUTO_KOT_PRINTED_TTL_MS = 4 * 60 * 60 * 1000;
const AUTO_KOT_LOCK_TTL_MS = 20 * 1000;
const AUTO_KOT_FAILURE_COOLDOWN_MS = 20 * 1000;

function readJsonStorage(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJsonStorage(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage write errors (private mode/full storage)
  }
}

function getLocalPrintServiceUrls() {
  const urls = [];
  const seen = new Set();
  const add = (value) => {
    if (!value) return;
    const normalized = String(value).replace(/\/$/, '');
    if (seen.has(normalized)) return;
    seen.add(normalized);
    urls.push(normalized);
  };

  const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
    ? String(window.location.origin).replace(/\/$/, '')
    : '';
  const port = (typeof window !== 'undefined' && window.location)
    ? String(window.location.port || '')
    : '';

  if (port === '9101') {
    add(origin);
  }

  add(process.env.REACT_APP_PRINT_AGENT_URL);
  add('http://localhost:9101');
  add('http://127.0.0.1:9101');
  return urls;
}

function Layout({ children, title, subtitle }) {
  const { sidebarWidth, isToggling } = useSidebar();
  const [kotConfig, setKotConfig] = useState({
    kitchenMode: 'kds',
    kotPrintMode: 'manual',
    restaurantName: 'Restaurant',
    paperWidthMm: 58,
    preferredPrinter: ''
  });

  const autoKotRunningRef = useRef(false);
  const inflightOrderKeysRef = useRef(new Set());
  const retryAfterRef = useRef(new Map());

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return false;
  });

  useEffect(() => {
    // Check if desktop
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', checkDesktop);
    
    return () => {
      window.removeEventListener('resize', checkDesktop);
    };
  }, []);

  const isKotAutoMode = kotConfig.kitchenMode === 'kot' && kotConfig.kotPrintMode === 'auto';

  const fetchKotConfig = async () => {
    try {
      const response = await api.get('/api/settings/restaurant');
      const data = response.data || {};

      const kitchenMode = String(data.kitchen_mode || 'kds').toLowerCase() === 'kot' ? 'kot' : 'kds';
      const kotPrintMode = String(data.kot_print_mode || 'manual').toLowerCase() === 'auto' ? 'auto' : 'manual';

      const configuredWidth = Number(data.printer_paper_width_mm || 58);
      const paperWidthMm = [58, 76, 80].includes(configuredWidth) ? configuredWidth : 58;

      setKotConfig({
        kitchenMode,
        kotPrintMode,
        restaurantName: String(data.name || 'Restaurant').trim() || 'Restaurant',
        paperWidthMm,
        preferredPrinter: String(data.printer_preferred_name || '').trim()
      });
    } catch {
      // Keep last known config if settings fetch fails.
    }
  };

  useEffect(() => {
    fetchKotConfig();
    const intervalId = setInterval(fetchKotConfig, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const cleanupPrintedSignatures = () => {
    if (typeof window === 'undefined') return {};
    const now = Date.now();
    const current = readJsonStorage(AUTO_KOT_PRINTED_KEY, {});
    const cleaned = {};

    Object.entries(current || {}).forEach(([key, value]) => {
      if (typeof value === 'number' && now - value < AUTO_KOT_PRINTED_TTL_MS) {
        cleaned[key] = value;
      }
    });

    writeJsonStorage(AUTO_KOT_PRINTED_KEY, cleaned);
    return cleaned;
  };

  const hasPrintedSignature = (signature) => {
    if (typeof window === 'undefined') return false;
    const map = cleanupPrintedSignatures();
    return typeof map[signature] === 'number';
  };

  const markPrintedSignature = (signature) => {
    if (typeof window === 'undefined') return;
    const map = cleanupPrintedSignatures();
    map[signature] = Date.now();
    writeJsonStorage(AUTO_KOT_PRINTED_KEY, map);
  };

  const acquireCrossTabOrderLock = (orderKey, signature) => {
    if (typeof window === 'undefined') return { acquired: true, ownerId: 'server' };

    const now = Date.now();
    const storageKey = `${AUTO_KOT_LOCK_PREFIX}${orderKey}`;
    const ownerId = `${now}-${Math.random().toString(36).slice(2, 10)}`;
    const current = readJsonStorage(storageKey, null);

    if (current && typeof current.expiresAt === 'number' && current.expiresAt > now) {
      return { acquired: false, ownerId: null };
    }

    writeJsonStorage(storageKey, {
      ownerId,
      signature,
      expiresAt: now + AUTO_KOT_LOCK_TTL_MS
    });

    const verify = readJsonStorage(storageKey, null);
    if (verify?.ownerId === ownerId) {
      return { acquired: true, ownerId };
    }

    return { acquired: false, ownerId: null };
  };

  const releaseCrossTabOrderLock = (orderKey, ownerId) => {
    if (typeof window === 'undefined') return;
    const storageKey = `${AUTO_KOT_LOCK_PREFIX}${orderKey}`;
    const current = readJsonStorage(storageKey, null);
    if (current?.ownerId === ownerId) {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    }
  };

  const groupKotItems = (items) => {
    const grouped = new Map();
    for (const item of items) {
      const name = item?.item_name || 'Item';
      const note = item?.special_instructions || item?.specialInstructions || item?.special_instruction || '';
      const qty = Number(item?.quantity || 1);
      const key = `${name}||${note}`;

      if (grouped.has(key)) {
        grouped.get(key).qty += qty;
      } else {
        grouped.set(key, { qty, name, special_instructions: note });
      }
    }
    return Array.from(grouped.values());
  };

  const buildOrderSignature = (order) => {
    const grouped = groupKotItems(order.items || []);
    const normalizedLines = grouped
      .map((line) => `${line.name}::${line.special_instructions || ''}::${Number(line.qty || 0)}`)
      .sort()
      .join('|');
    return `${order.orderId || order.orderNumber}::${normalizedLines}`;
  };

  const postToLocalPrintService = async (job) => {
    const urls = getLocalPrintServiceUrls();
    let lastError = null;

    for (const baseUrl of urls) {
      try {
        const response = await fetch(`${baseUrl}/print`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(job)
        });

        if (!response.ok) {
          let message = `HTTP ${response.status}`;
          try {
            const data = await response.clone().json();
            message = data.error || data.message || message;
          } catch {
            // ignore json parse errors
          }
          lastError = new Error(message);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/octet-stream')) {
          lastError = new Error('Printer not found on local print service');
          continue;
        }

        const result = await response.json().catch(() => ({}));
        if (!result?.success) {
          lastError = new Error(result?.error || result?.message || 'Print failed');
          continue;
        }

        return true;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('No local print service is reachable');
  };

  const printAndAdvanceOrder = async (order) => {
    const orderKey = String(order.orderId || order.orderNumber || 'unknown');
    const signature = buildOrderSignature(order);
    const retryAfter = retryAfterRef.current.get(signature) || 0;
    if (Date.now() < retryAfter) {
      return;
    }

    if (hasPrintedSignature(signature)) {
      return;
    }

    if (inflightOrderKeysRef.current.has(orderKey)) {
      return;
    }

    const lock = acquireCrossTabOrderLock(orderKey, signature);
    if (!lock.acquired) {
      return;
    }

    inflightOrderKeysRef.current.add(orderKey);

    try {
      const printJob = {
        template: 'kot',
        restaurant: kotConfig.restaurantName || 'Restaurant',
        paperWidthMm: kotConfig.paperWidthMm || 58,
        printer: {
          type: 'windows',
          connection: kotConfig.preferredPrinter || 'auto'
        },
        kot_order_number: order.orderNumber || order.orderId,
        table: order.tableNumber || 'N/A',
        date: order.displayTime || new Date().toISOString(),
        items: groupKotItems(order.items || []),
        timestamp: Date.now()
      };

      try {
        await postToLocalPrintService(printJob);
      } catch (primaryPrintError) {
        // Retry once using auto connection in case saved printer queue is stale/unavailable.
        if (printJob?.printer?.connection && printJob.printer.connection !== 'auto') {
          printJob.printer.connection = 'auto';
          await postToLocalPrintService(printJob);
        } else {
          throw primaryPrintError;
        }
      }

      const pendingItems = (order.items || []).filter((item) => item?.status === 'pending' && item?.id);
      await Promise.all(pendingItems.map((item) => api.patch(`/api/kds/items/${item.id}/status`, { status: 'preparing' })));
      markPrintedSignature(signature);
      retryAfterRef.current.delete(signature);
    } catch (error) {
      console.error(`Auto KOT print failed for order ${orderKey}:`, error);
      retryAfterRef.current.set(signature, Date.now() + AUTO_KOT_FAILURE_COOLDOWN_MS);
    } finally {
      inflightOrderKeysRef.current.delete(orderKey);
      releaseCrossTabOrderLock(orderKey, lock.ownerId);
    }
  };

  const runAutoKotCycle = async () => {
    if (!isKotAutoMode || autoKotRunningRef.current) {
      return;
    }

    autoKotRunningRef.current = true;
    try {
      const response = await api.get('/api/kds/orders');
      const items = Array.isArray(response.data) ? response.data : [];

      const pendingItems = items.filter((item) => item?.status === 'pending');
      if (pendingItems.length === 0) {
        return;
      }

      const byOrder = new Map();
      pendingItems.forEach((item) => {
        const orderId = item?.order_id || item?.orders?.id || item?.orderId;
        const orderNumber = item?.orders?.order_number || `Order-${orderId}`;
        const key = String(orderId || orderNumber);
        if (!byOrder.has(key)) {
          byOrder.set(key, {
            orderId,
            orderNumber,
            tableNumber: item?.table_number || 'N/A',
            displayTime: item?.display_time || item?.created_at || new Date().toISOString(),
            items: []
          });
        }
        byOrder.get(key).items.push(item);
      });

      const orders = Array.from(byOrder.values());
      for (const order of orders) {
        // Process serially so each order gets exactly one grouped KOT print attempt per cycle.
        await printAndAdvanceOrder(order);
      }
    } catch (error) {
      console.error('Auto KOT cycle failed:', error);
    } finally {
      autoKotRunningRef.current = false;
    }
  };

  useEffect(() => {
    if (!isKotAutoMode) {
      return undefined;
    }

    runAutoKotCycle();
    const intervalId = setInterval(runAutoKotCycle, 4000);
    return () => clearInterval(intervalId);
  }, [isKotAutoMode, kotConfig.kitchenMode, kotConfig.kotPrintMode, kotConfig.paperWidthMm, kotConfig.preferredPrinter, kotConfig.restaurantName]);

  return (
    <div className="h-screen bg-gray-50 flex flex-col lg:flex-row overflow-hidden">
      <Sidebar />
      <div 
        className="flex-1 flex flex-col min-w-0 w-full lg:w-auto overflow-hidden" 
        id="main-content"
        style={{ 
          marginLeft: isDesktop ? `${sidebarWidth}px` : '0',
          transition: isToggling ? 'margin-left 0.3s ease-in-out' : 'none'
        }}
      >
        <Topbar />
        <div className="flex-1 p-3 sm:p-4 md:p-5 lg:p-6 xl:p-8 2xl:p-10 overflow-y-auto">
          <div className="max-w-[1920px] mx-auto w-full">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Layout;

